import { getLogger } from '@logtape/logtape';
import { Button } from '@mantine/core';
import { ReactElement, useMemo, useState } from 'react';

import PatientDuplicationPopup from '@/components/AppointmentScheduler/PatientDuplicationPopup.tsx';
import { RescheduleNotificationModal } from '@/components/AppointmentScheduler/reschedule-notification-modal.tsx';
import { SeriesFooterButtons } from '@/components/AppointmentScheduler/series-footer-buttons.tsx';
import { ChainFooterButtons } from '@/components/chain-booking/chain-footer-buttons.tsx';

import { revalidateAppointmentsRangeCache } from '@/hooks/use-appointments-for-date-range.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { GenericPatient } from '@/models/GenericPatient.ts';
import { AppointmentBookingFormValues } from '@/models/appointment/AppointmentBookingFormValues.ts';
import { EnrichedAppointmentBookingFormValues } from '@/models/appointment/EnrichedAppointmentBookingFormValues.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { getCorrectInsuranceForForms, onBookOrEditAppointment } from '@/services/appointmentService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

import { selectCurrentForm, useBookingStore } from '@/stores/bookingStore.ts';

import { pluralizeTermine } from '@/utils/collection-utils.ts';
import { compareObjectsAndReturnDifferentFields } from '@/utils/objectUtils.ts';
import { resolvePatientUserId } from '@/utils/patientUtils.ts';

const logger = getLogger(['component', 'appointment-scheduler-footer']);

interface Props {
    onClose?: () => void;
}

const AppointmentSchedulerFooter = ({ onClose }: Props) => {
    const {
        initial_form_values,
        appointments_form_draft,
        is_last_form_valid,
        is_booking_loading,
        is_validating_insurance,
        editing_appointment,
        current_patient,
        patient_duplication_allowed,
        isSeriesMode,
        is_chain_mode,
    } = useBookingStore((state) => state);
    const currentForm = useBookingStore(selectCurrentForm);
    const { data: enriched_practitioner_data } = useEnrichedPractitionerDataForSpecificInstance(
        current_patient?.instance_id ?? ''
    );
    const { selected_customer, selected_location } = useConfig();
    const [show_confirm_popup, setShowConfirmPopup] = useState(false);
    const [show_reschedule_modal, setShowRescheduleModal] = useState(false);

    const updated_fields: Set<keyof AppointmentBookingFormValues> = useMemo(
        () =>
            currentForm ? compareObjectsAndReturnDifferentFields(initial_form_values ?? {}, currentForm) : new Set(),
        [initial_form_values, currentForm]
    );

    const handleClose = (): void => {
        if (is_booking_loading) {
            return;
        }

        if (onClose) onClose();
    };

    // TODO: extract handleSubmit into a custom hook (e.g. useAppointmentSchedulerFooter) to match use-book-appointment-collection pattern
    // TODO: add test coverage for handleSubmit error paths (insurance validation failure, booking failure)
    const submitAppointment = async (sendNotification?: boolean): Promise<void> => {
        const typed_patient = current_patient as GenericPatient;

        const user_id = resolvePatientUserId(typed_patient);

        if (!user_id) {
            showErrorNotification('Benutzer-ID ist ungültig'); // User ID is not valid
            return;
        }

        const instance_id: string | undefined = current_patient?.instance_id;
        if (!enriched_practitioner_data || !user_id || !instance_id) return;

        if (enriched_practitioner_data.pvs_provider === PVSProvider.DOC_CIRRUS) {
            let result;
            try {
                result = await getCorrectInsuranceForForms(
                    appointments_form_draft.map((form) => ({ ...form, form_id: form.id })),
                    selected_customer.customer_id,
                    instance_id,
                    user_id,
                    patient_duplication_allowed
                );
            } catch (error) {
                logger.error('Failed to validate insurance', { error: error });
                // Reset both flags defensively: getCorrectInsuranceForForms sets is_validating_insurance,
                // and onBookOrEditAppointment sets is_booking_loading — reset both to avoid stuck state
                useBookingStore.setState({ is_booking_loading: false, is_validating_insurance: false });
                showErrorNotification('Termin konnte nicht gebucht werden. Bitte versuchen Sie es erneut.');
                return;
            }

            logger.debug('Insurance validation result', { result: result });

            if (result.duplication_required && !result.duplication_done) {
                // Ask to allow patient duplication
                setShowConfirmPopup(true);
            } else {
                try {
                    await onBookOrEditAppointment(
                        selected_customer,
                        selected_location,
                        result.forms,
                        enriched_practitioner_data,
                        instance_id,
                        { send_notification: sendNotification }
                    );
                    revalidateAppointmentsRangeCache();
                } catch (error) {
                    logger.error('Failed to book appointment', { error: error });
                    // Reset both flags defensively: getCorrectInsuranceForForms sets is_validating_insurance,
                    // and onBookOrEditAppointment sets is_booking_loading — reset both to avoid stuck state
                    useBookingStore.setState({ is_booking_loading: false, is_validating_insurance: false });
                    showErrorNotification('Termin konnte nicht gebucht werden. Bitte versuchen Sie es erneut.');
                }
            }
        } else {
            const enriched_forms: EnrichedAppointmentBookingFormValues[] = appointments_form_draft.map((form) => ({
                ...form,
                form_id: form.id,
                pvs_user_id: typed_patient.pvs_user_id,
                eterno_user_id: typed_patient.eterno_user_id,
            }));

            try {
                await onBookOrEditAppointment(
                    selected_customer,
                    selected_location,
                    enriched_forms,
                    enriched_practitioner_data,
                    instance_id,
                    { send_notification: sendNotification }
                );
                revalidateAppointmentsRangeCache();
            } catch (error) {
                logger.error('Failed to book appointment', { error: error });
                // Reset both flags defensively: getCorrectInsuranceForForms sets is_validating_insurance,
                // and onBookOrEditAppointment sets is_booking_loading — reset both to avoid stuck state
                useBookingStore.setState({ is_booking_loading: false, is_validating_insurance: false });
                showErrorNotification('Termin konnte nicht gebucht werden. Bitte versuchen Sie es erneut.');
            }
        }
    };

    const handleSubmit = (): void => {
        const isReschedule = !!editing_appointment && updated_fields.has('date_time');
        if (isReschedule) {
            setShowRescheduleModal(true);
        } else {
            void submitAppointment();
        }
    };

    // Save if there are new appointment or close
    const getSubmitButton = (): ReactElement => {
        const is_disabled =
            is_booking_loading ||
            is_validating_insurance ||
            (appointments_form_draft.length > 0 && !is_last_form_valid) ||
            (appointments_form_draft.length > 0 && editing_appointment && updated_fields.size === 0);

        let submit_btn_text = 'Schließen'; // close by default
        if (appointments_form_draft.length > 0) {
            // Save
            submit_btn_text = `${pluralizeTermine(appointments_form_draft.length)} speichern`;
            return (
                <Button disabled={is_disabled} onClick={handleSubmit}>
                    {submit_btn_text}
                </Button>
            );
        } else {
            return (
                <Button disabled={is_disabled} onClick={() => handleClose()}>
                    {submit_btn_text}
                </Button>
            );
        }
    };

    const submit_button = getSubmitButton();

    if (isSeriesMode) {
        return <SeriesFooterButtons onClose={onClose} />;
    }

    if (is_chain_mode) {
        return <ChainFooterButtons />;
    }

    return (
        <div className="flex w-full justify-end">
            <PatientDuplicationPopup
                setVisible={setShowConfirmPopup}
                visible={show_confirm_popup}
                setConfirm={() => useBookingStore.setState({ patient_duplication_allowed: true })}
            />
            <RescheduleNotificationModal
                visible={show_reschedule_modal}
                setVisible={setShowRescheduleModal}
                onConfirm={() => {
                    void submitAppointment(true);
                }}
                onCancel={() => {
                    void submitAppointment(false);
                }}
            />
            {submit_button}
        </div>
    );
};

export default AppointmentSchedulerFooter;
